/**
 * One column header cell (spec M1-B4 §8.2).
 *
 * Measured: 36 px tall inside the 76 px group header, text 14 px / 20 px weight 400
 * `rgb(50,51,56)`, horizontally centred, 1 px `#d0d4e4` separator on the right
 * `[ui:first-look/styles.json]`, `[ui:library/table-01-top.png]`.
 *
 * It carries all four header interactions: inline rename (double-click), the `⌄` menu, the
 * drag handle for reordering and the 5 px resize gutter on the right edge.
 *
 * **P-292 — the sort control is a hover widget, not the title.** Measured on the sandbox board
 * (`discovery/behavior-notes/P-292-header-click-sort.md`): clicking the header *title* sorts
 * nothing (a double-click renames), and monday's sort affordance is a 20 × 20 circular button
 * centred on the header and straddling its top edge — `rgb(246,247,251)` unsorted,
 * `rgb(0,115,234)` with white glyphs while the column is sorted, shadow
 * `rgba(51,51,51,.16) 0 1px 8px 1px`, radius 10. Clicking it cycles **ascending → descending →
 * ascending**; it never returns to unsorted, because a white **`clear`** pill appears to its left
 * while it is sorted (and a `save` one to its right, which for us is the toolbar pane's "Save to
 * this view" — `[ui:library/table-16-sort-widget-active.png]`). Its accessible name is monday's
 * own: `Sort by X` → `Sorted by X in ascending order`.
 */
import type { ColumnId } from '@elizmonday/shared';
import { Icon, icons } from '@elizmonday/ui';
import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { getColumnUi } from '../columns/registry';
import type { BoardColumn } from '../data/types';
import { measureColumnWidth } from './autosize';
import { clampWidth, COLUMN_WIDTH_MIN } from './layout';

export interface ColumnHeaderProps {
  column: BoardColumn;
  /** The box this header paints — for `name` the **cell**, not the stored width (P-021). */
  width: number;
  /**
   * `stored - width`: what a drag has to add back before it writes monday's number. It is 0
   * for every ordinary column and `NAME_STICKY_CHROME_WIDTH` (104) for the name column, whose
   * stored width is the whole sticky region (P-021).
   */
  widthOffset?: number;
  /** Resize floor in **stored** units; the name column's is higher than `COLUMN_WIDTH_MIN`. */
  minWidth?: number;
  left?: number;
  sticky?: boolean;
  sorted?: 'asc' | 'desc' | null;
  filtered?: boolean;
  /**
   * P-292: the header's own sort. `direction` is what the click resolved to — the next step of
   * monday's asc → desc cycle — and `null` is the ✕ that clears it.
   */
  onSort?(direction: 'asc' | 'desc' | null): void;
  onRename(title: string): void;
  onResize(width: number): void;
  onOpenMenu(x: number, y: number): void;
  /**
   * The column that follows this one in the board's order, or `null` when this is the last.
   * A drop on the **right** half lands before it — which is what "after this header" is in
   * the grid's own before-vocabulary (P-101).
   */
  nextColumnId?: ColumnId | null;
  /**
   * **P-100.** Both halves of the gesture: the column the user **picked up**, and the one it
   * was dropped before (`null` = last). The drop used to report only the position, and the
   * header that handled it supplied the subject from its own closure — i.e. the drop
   * **target** — so every column drag moved the wrong column.
   */
  onDropBefore(movedColumnId: ColumnId, beforeColumnId: ColumnId | null): void;
  draggable?: boolean;
}

export function ColumnHeader({
  column,
  width,
  widthOffset = 0,
  minWidth = COLUMN_WIDTH_MIN,
  left,
  sticky = false,
  sorted = null,
  filtered = false,
  onSort,
  onRename,
  onResize,
  onOpenMenu,
  onDropBefore,
  nextColumnId = null,
  draggable = true,
}: ColumnHeaderProps) {
  const [editing, setEditing] = useState(false);
  const [dropSide, setDropSide] = useState<'left' | 'right' | null>(null);
  const previewWidth = useRef<number | null>(null);
  const [livePreview, setLivePreview] = useState<number | null>(null);
  const ui = getColumnUi(column.type);

  const startResize = (event: ReactPointerEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    /* Everything below is in **stored** units, so a drag writes the number monday keeps. */
    const startWidth = width + widthOffset;
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);

    const onMove = (move: PointerEvent) => {
      const next = clampWidth(startWidth + (move.clientX - startX), minWidth);
      previewWidth.current = next;
      setLivePreview(next - widthOffset);
    };
    const onUp = () => {
      target.releasePointerCapture(event.pointerId);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const next = previewWidth.current;
      previewWidth.current = null;
      setLivePreview(null);
      // Commit on mouse-up only — the live preview never writes (§8.2).
      if (next !== null && next !== startWidth) onResize(next);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return (
    <div
      className="ez-column-header"
      data-testid={`column-header-${column.id}`}
      data-sticky={sticky || undefined}
      data-drop={dropSide ?? undefined}
      role="columnheader"
      aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none'}
      style={
        sticky ? { width: livePreview ?? width } : { left, width: livePreview ?? width, position: 'absolute' }
      }
      draggable={draggable && !editing}
      onDragStart={(event) => event.dataTransfer.setData('text/ez-column', column.id)}
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes('text/ez-column')) return;
        event.preventDefault();
        const box = event.currentTarget.getBoundingClientRect();
        setDropSide(event.clientX - box.left < box.width / 2 ? 'left' : 'right');
      }}
      onDragLeave={() => setDropSide(null)}
      onDrop={(event) => {
        event.preventDefault();
        const moved = event.dataTransfer.getData('text/ez-column');
        const side = dropSide;
        setDropSide(null);
        if (!moved || moved === column.id) return;
        /*
         * The **left** half drops before this column; the **right** half drops before the one
         * that follows it, which is "last" — `null` — when nothing does. The source is the id
         * on the dataTransfer, never this header's own (P-100).
         */
        onDropBefore(moved as ColumnId, side === 'left' ? column.id : nextColumnId);
      }}
    >
      {onSort ? (
        <span className="ez-column-header__quick" data-sorted={sorted ?? undefined}>
          <button
            type="button"
            className="ez-column-header__sort"
            data-testid={`column-sort-${column.id}`}
            data-direction={sorted ?? 'none'}
            aria-label={
              sorted === null
                ? `Sort by ${column.title}`
                : `Sorted by ${column.title} in ${sorted === 'asc' ? 'ascending' : 'descending'} order`
            }
            /* The cycle monday's widget has: unsorted → ascending → descending → ascending. */
            onClick={(event) => {
              event.stopPropagation();
              onSort(sorted === 'asc' ? 'desc' : 'asc');
            }}
          >
            <Icon icon={icons.sort} size={14} ignoreFocusStyle />
          </button>
          {sorted === null ? null : (
            <button
              type="button"
              className="ez-column-header__sort-clear"
              data-testid={`column-sort-clear-${column.id}`}
              aria-label={`Clear sort on ${column.title}`}
              onClick={(event) => {
                event.stopPropagation();
                onSort(null);
              }}
            >
              clear
            </button>
          )}
        </span>
      ) : null}

      <span className="ez-column-header__icon" aria-hidden="true">
        <Icon icon={icons[ui.icon]} size={16} ignoreFocusStyle />
      </span>

      {editing ? (
        <input
          className="ez-column-header__input"
          data-testid={`column-rename-${column.id}`}
          autoFocus
          defaultValue={column.title}
          aria-label={`Rename ${column.title}`}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === 'Enter') {
              onRename((event.target as HTMLInputElement).value.trim() || column.title);
              setEditing(false);
            }
            if (event.key === 'Escape') setEditing(false);
          }}
          onBlur={(event) => {
            onRename(event.target.value.trim() || column.title);
            setEditing(false);
          }}
        />
      ) : (
        <span className="ez-column-header__title" onDoubleClick={() => setEditing(true)} title={column.title}>
          {column.title}
        </span>
      )}

      {filtered ? <span className="ez-column-header__badge" aria-label="Filtered" /> : null}

      <button
        type="button"
        className="ez-column-header__menu"
        aria-label={`${column.title} column menu`}
        onClick={(event) => {
          const box = (event.currentTarget as HTMLElement).getBoundingClientRect();
          onOpenMenu(box.left, box.bottom);
        }}
      >
        <Icon icon={icons.chevronDown} size={14} ignoreFocusStyle />
      </button>

      <span
        className="ez-column-header__resize"
        data-testid={`column-resize-${column.id}`}
        role="separator"
        aria-label={`Resize ${column.title}`}
        aria-orientation="vertical"
        onPointerDown={startResize}
        /*
         * **P-107.** Autosize to what is *in* the column, not to the type's registry default:
         * the widest mounted cell, the header title included, clamped to the type's floor
         * (§8.2, ASSUMED Q165). An empty column still falls back to `ui.defaultWidth`, which
         * is the only number there is when there is nothing to measure.
         */
        onDoubleClick={() =>
          onResize(
            // The measurement is of the **cell**; `widthOffset` turns it into the number
            // monday stores, which for the name column is the whole sticky region (P-021).
            clampWidth(
              measureColumnWidth({
                columnId: column.id,
                headerTitle: column.title,
                fallback: ui.defaultWidth,
              }) + widthOffset,
              minWidth,
            ),
          )
        }
      />
    </div>
  );
}
